
import { Delegates, Events as eventApi, Invoices, Quotes } from "@/api";
import { OfficeType } from "@/api/types";
import Card from "@/components/common/Card.vue";
import GoogleAddressAutocomplete from "@/components/common/GoogleAddressAutocomplete.vue";
import BookingDeleteDialog from "@/components/events/BookingDeleteDialog.vue";
import PageTitle from "@/components/common/PageTitle.vue";
import DelegatesList from "@/components/delegates/DelegatesList.vue";
import Spinner from "@/components/shared/Spinner.vue";
import {
  DistancePoint,
  GooglePlace,
  getDrivingDistance,
  metersToKm
} from "@/utils/googleMaps";
import {
  DEFAULT_OFF_SITE_RATES,
  OffSiteRates,
  mapOffSiteRates
} from "@/utils/offSiteRates";
import {
  EVENT_TYPE_AUDIT,
  EVENT_TYPE_ONLINE_COURSE,
  OFFICE_TYPE_ONLINE
} from "@/utils/constants";
import { quoteBookingCompanies } from "@/utils/quoteCourses";
import { calculateCustomerTransportCharge } from "@/utils/transport";
import moment from "moment";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue, Watch } from "vue-property-decorator";

import CompanyPrice from "@/components/events/CompanyPrice.vue";
import BookingContactFields from "@/components/events/BookingContactFields.vue";
import EventExpenseSetup from "@/components/events/EventExpenseSetup.vue";
import ScheduleQualification from "@/components/events/ScheduleQualification.vue";
import { namespace } from "vuex-class";

const Events = namespace("events");
const Auth = namespace("auth");
const Company = namespace("company");
const Instructor = namespace("instructors");

@Component({
  components: {
    DelegatesList,
    Card,
    PageTitle,
    ValidationObserver,
    ValidationProvider,
    CompanyPrice,
    BookingContactFields,
    EventExpenseSetup,
    Spinner,
    ScheduleQualification,
    GoogleAddressAutocomplete,
    BookingDeleteDialog
  }
})
export default class EditEventPage extends Vue {
  deleteDialogOpen = false;
  deleting = false;

  get instructorIsOutsource(): boolean {
    return Boolean((this.event as any)?.instructor?.isOutsource);
  }

  OCCUPATIONAL_QUALIFICATION_ID = 8;

  currentDate = moment().format("YYYY-MM-DD");
  nextDay = moment()
    .add(1, "days")
    .format("YYYY-MM-DD");
  selectedDelegates: Array<{ id: number; companyId: number }> = [];
  bookingTypes = [];
  quotes: any[] = [];
  quoteSearch = "";
  quoteLoading = false;
  quoteRequestId = 0;
  private quoteSearchTimer: number | null = null;
  courseDurationId = 1;
  courseOverview: Array<{ title: string; content: any }> = [];
  isSingleSelect = false;
  addressPrepopulate = "";
  distanceText = "";
  distanceLoading = false;
  offSiteRates: OffSiteRates = { ...DEFAULT_OFF_SITE_RATES };
  // Guards the Transport auto-formula so it doesn't overwrite the stored
  // value while an existing event is being loaded (only user edits recalc).
  transportAutoReady = false;
  tableHeader = {
    title: "Assign delegates",
    icon: "mdi-account"
  };
  menu2 = false;
  menu3 = false;
  menu4 = false;
  menu = false;
  companyPriceValid = false;
  durationVal: any = 0;
  selectedCourseCatId: any = null;

  eventStartDate = moment().format("YYYY-MM-DD");
  headersTitle = [{ icon: "mdi-file-edit", header: "Edit booking Request" }];
  loading = false;
  fetchLoading = false;

  //Action
  @Events.Action("getCoursesList")
  getCoursesList!: () => Promise<void>;
  @Events.Action("getAuditsList")
  getAuditsList!: () => Promise<any>;
  @Events.Action("getOnlineCoursesList")
  private getOnlineCoursesList!: () => Promise<any>;
  @Events.Action("getEventTypes")
  getEventTypes!: () => Promise<any>;
  @Events.Action("getOfficeTypes")
  getOfficeTypes!: () => Promise<void>;
  @Events.Action("getCoursesDurations")
  getCoursesDurations!: () => Promise<void>;
  @Events.Action("getCoursesInfo")
  getCoursesInfo!: (id: string) => Promise<void>;
  @Events.Action("getAuditInfo")
  private getAuditInfo!: (id: string) => Promise<any>;
  @Events.Action("getOnlineCoursesInfo")
  private getOnlineCoursesInfo!: (id: string) => Promise<any>;
  @Events.Action("updEvent")
  updEvent!: (params: {
    eventData: any;
    selectedDelegates: any;
  }) => Promise<void>;
  @Instructor.Action("getInstructorslistByCourseID")
  private getInstructorslistByCourseID!: ({
    id,
    active
  }: {
    id: number;
    active: string | number;
  }) => Promise<any>;
  @Instructor.Action("getInstructorslistByAuditID")
  private getInstructorslistByAuditID!: ({
    id,
    active
  }: {
    id: number;
    active: string | number;
  }) => Promise<any>;
  @Instructor.Action("getInstructorslistByOnlineCourseID")
  private getInstructorslistByOnlineCourseID!: ({
    id,
    active
  }: {
    id: number;
    active: string | number;
  }) => Promise<any>;
  @Company.Action("getListCompanyWithPagination")
  getListCompanyWithPagination!: (data: any) => Promise<void>;
  @Instructor.Action("getInstructorsListWithoutPagination")
  getInstructorsListWithoutPagination!: () => Promise<void>;
  @Events.Action("getEventById")
  getEventById!: (eventId: string) => Promise<any>;
  @Events.Action("removeEvent")
  removeEvent!: (payload: {
    eventId: string | number;
    reason: string;
    instructorArrived: boolean | null;
  }) => Promise<void>;

  // mutations
  @Events.Mutation("startFetch")
  eventStartFetch!: () => void;
  @Company.Mutation("startFetch")
  companyStartFetch!: () => void;
  @Events.Mutation("setEventType")
  setEventType!: (type: any) => void;
  @Events.Mutation("updCompanies")
  updCompanies!: (data: any) => void;
  @Instructor.Mutation("setInstructors")
  setInstructors!: (data: any) => void;
  @Events.Mutation("setOfflineTrainingRegister")
  setOfflineTrainingRegister!: (item: any) => void;

  //STATE
  @Events.State("courses") courses: any;
  @Events.State("audits") audits: any;
  @Events.State("onlineCourses") onlineCourses: any;
  @Events.State("officeTypes") officeTypes: any;
  @Events.State("event") event: any;
  @Events.State("durations") durations: any;
  @Events.State("coursesInfo") coursesInfo: any;
  @Instructor.State("instructors") instructors: any;

  //GETTERS
  @Auth.Getter("getUser")
  getUser!: object | null;

  async created() {
    this.loading = true;
    try {
      this.eventStartFetch();
      this.companyStartFetch();
      await this.loadOffSiteRates();
      // await this.getCoursesList();
      await this.fetchData();
    } catch (e) {
      await this.$error(e);
    }

    if (!this.canEdit) {
      this.$router.push({ name: "CourseEvents" });
    }

    this.loading = false;
  }

  async loadOffSiteRates(): Promise<void> {
    try {
      this.offSiteRates = mapOffSiteRates(await Invoices.rates());
    } catch (e) {
      this.offSiteRates = { ...DEFAULT_OFF_SITE_RATES };
    }
  }
  async onCreated() {
    // await this.getEventById(this.eventId);
    const info = await eventApi.info(this.eventId);

    this.setOfflineTrainingRegister(info.offlineTrainings);

    // this.event.offlineTrainings.push({ ...data, delegates: [] });
  }
  onDeleteOfflineTraining(id: number) {
    this.event.offlineTrainings = this.event.offlineTrainings.filter(
      (el: any) => el.id != id
    );
  }

  async fetchData() {
    const loadedEvent = await this.getEventById(this.eventId);
    this.bookingTypes = await this.getEventTypes();

    await this.getOfficeTypes();

    await this.getCoursesDurations();
    await this.getListCompanyWithPagination(!!loadedEvent.company.length);
    await this.getInstructorsListWithoutPagination();

    const courseType = this.bookingTypes.find(
      (type: any) => type.id === this.event.eventTypeId
    );

    this.setEventType(courseType);
    await this.handleCourseChange();
    this.eventStartDate = moment(
      this.event.eventStartDate,
      "DD-MM-YYYY"
    ).format("YYYY-MM-DD");
    await this.getDelegateByEvent();
    // if (this.event?.eventType?.name === "Course") {
    this.durationVal = this.event.duration;
    this.onDurationSelect();
    // }

    this.updCompanies(loadedEvent.company);

    // Office address is the distance origin (company office -> venue).
    try {
      const loadedAddress = await eventApi.getOfficeAddress();
      if (loadedAddress.data && loadedAddress.data.length > 0) {
        this.addressPrepopulate = loadedAddress.data[0].address;
      }
    } catch (e) {
      this.addressPrepopulate = "";
    }
    const quoteIds = this.event.quoteIds?.length
      ? this.event.quoteIds
      : this.event.quoteId
      ? [this.event.quoteId]
      : [];
    this.$set(this.event, "quoteIds", quoteIds);
    this.$set(this.event, "quoteId", quoteIds[0] || null);
    await this.loadQuotes();
    // Show the distance already stored on the event (if any).
    if (this.event.distance) {
      this.distanceText = metersToKm(this.event.distance);
    }
    // Derive the Accommodation toggle from the stored cost (feature is new,
    // legacy events only carry the numeric accommodation value).
    this.$set(
      this.event,
      "accommodationIncluded",
      Number(this.event.accommodation) > 0
    );
    if (quoteIds.length) this.applySelectedQuotes();
    // Loading done — user edits may now drive the Transport auto-formula.
    this.transportAutoReady = true;
    await this.loadTollDocuments();
    this.recalculateTransport();
  }

  onVenueSelect(place: GooglePlace): void {
    this.event.address = place.address;
    this.event.placeId = place.placeId;
    this.computeDistance();
  }

  /**
   * The chosen instructor, but only when their profile says to measure travel
   * from where they live and an address is actually on file. One saved before
   * that address became mandatory falls back to the office.
   */
  get instructorTravellingFromHome(): any | null {
    if (!this.event.instructorId) {
      return null;
    }
    const instructor = (this.instructors || []).find(
      (item: any) => Number(item.id) === Number(this.event.instructorId)
    );
    return instructor &&
      instructor.calculate_travel_from_home &&
      instructor.home_address
      ? instructor
      : null;
  }

  /** Travel is measured from the company office unless the above says otherwise. */
  get travelOrigin(): DistancePoint {
    const instructor = this.instructorTravellingFromHome;
    return instructor
      ? {
          placeId: instructor.home_place_id || null,
          address: instructor.home_address
        }
      : { address: this.addressPrepopulate };
  }

  get isTravelFromInstructorHome(): boolean {
    return this.instructorTravellingFromHome !== null;
  }

  get distanceLabel(): string {
    return this.isTravelFromInstructorHome
      ? "Distance from instructor's home"
      : "Distance from office";
  }

  // Swapping the instructor can swap the origin, so the distance (and the
  // transport charge derived from it) has to be recomputed.
  @Watch("event.instructorId")
  onInstructorChangeForTravel(): void {
    // Not while the saved event is still being loaded — that would overwrite
    // the distance the booking was priced on.
    if (!this.transportAutoReady) {
      return;
    }
    this.computeDistance();
  }

  async computeDistance(): Promise<void> {
    // Distance is travel origin -> course venue (reference value, LMS-1236).
    const origin = this.travelOrigin;
    if (!origin.address || !this.event.address) {
      this.event.distance = null;
      this.distanceText = "";
      return;
    }
    this.distanceLoading = true;
    try {
      const result = await getDrivingDistance(origin, {
        placeId: this.event.placeId,
        address: this.event.address
      });
      if (result) {
        this.event.distance = result.meters;
        this.distanceText = metersToKm(result.meters);
      } else {
        this.event.distance = null;
        this.distanceText = "";
      }
    } catch (e) {
      this.event.distance = null;
      this.distanceText = "";
    } finally {
      this.distanceLoading = false;
      this.recalculateTransport();
    }
  }

  // Course duration in days — drives the Transport formula (LMS-1236).
  get durationDays(): number {
    if (this.selectedCourseCatId === 3 && this.event.duration) {
      const match = (this.durations || []).find(
        (d: any) => d.id === this.event.duration
      );
      // Duration names look like "5-day" -> parseInt extracts the leading number.
      const days = match ? parseInt(match.name, 10) : 0;
      return days > 0 ? days : 1;
    }
    const duration: any = this.coursesInfo && this.coursesInfo.duration;
    if (duration) {
      const days =
        typeof duration === "object"
          ? parseInt(duration.name, 10)
          : parseInt(duration, 10);
      return days > 0 ? days : 1;
    }
    return 1;
  }

  onAccommodationToggle(value: boolean): void {
    this.event.accommodationIncluded = value;
    if (value) {
      this.event.accommodationCheckIn =
        this.event.accommodationCheckIn || this.eventStartDate;
      this.event.accommodationCheckOut =
        this.event.accommodationCheckOut ||
        moment(this.eventStartDate)
          .add(this.durationDays - 1, "days")
          .format("YYYY-MM-DD");
    }
    // No accommodation -> no accommodation cost.
    if (!value) {
      this.event.accommodation = 0;
      this.event.accommodationName = "";
      this.event.accommodationAddress = "";
      this.event.accommodationCheckIn = "";
      this.event.accommodationCheckOut = "";
    }
    this.recalculateTransport();
  }

  @Watch("event.officeTypeId")
  onOfficeTypeChange(value: number): void {
    if (!this.transportAutoReady) {
      return;
    }
    if (Number(value) === 1) {
      this.event.address = this.addressPrepopulate;
      this.event.placeId = null;
      this.event.distance = null;
      this.distanceText = "";
      this.resetAdditionalCosts();
    } else {
      this.event.address = "";
      this.event.placeId = null;
      this.event.distance = null;
      this.distanceText = "";
    }
    this.recalculateTransport();
  }

  uploadingTollDocuments = false;
  tollDocuments: any[] = [];

  get canEditTollgateInfo(): boolean {
    const currentUser: any = this.getUser;
    return Boolean(
      currentUser &&
        currentUser.permissions &&
        currentUser.permissions.includes("edit_booking_tollgate_info")
    );
  }

  async loadTollDocuments(): Promise<void> {
    if (!this.canEditTollgateInfo || !this.eventId) return;
    try {
      this.tollDocuments = await eventApi.tollDocuments(this.eventId);
    } catch (e) {
      await this.$error(e);
    }
  }

  async uploadTollDocuments(files: File[]): Promise<void> {
    if (!this.eventId) return;
    this.uploadingTollDocuments = true;
    try {
      this.tollDocuments = await eventApi.uploadTollDocuments(
        this.eventId,
        files
      );
      await this.$success(
        "<strong>Success!</strong> Toll gate documents have been uploaded."
      );
    } catch (e) {
      await this.$error(e);
    } finally {
      this.uploadingTollDocuments = false;
    }
  }

  async viewTollDocument(tollDocument: any): Promise<void> {
    const previewWindow = window.open("", "_blank");
    try {
      const blob = await eventApi.downloadTollDocument(
        this.eventId,
        tollDocument.id
      );
      const url = window.URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (previewWindow) previewWindow.close();
      await this.$error(e);
    }
  }

  resetAdditionalCosts(): void {
    this.event.accommodationIncluded = false;
    this.event.accommodation = 0;
    this.event.accommodationName = "";
    this.event.accommodationAddress = "";
    this.event.accommodationCheckIn = "";
    this.event.accommodationCheckOut = "";
    this.event.claimFuelAmount = 0;
    this.event.claimSntAmount = 0;
    this.event.claimSntIncluded = false;
    this.event.claimBreakfastIncluded = false;
    this.event.claimDinnerIncluded = false;
    this.event.claimSntQty = 0;
    this.event.claimBreakfastQty = 0;
    this.event.claimDinnerQty = 0;
    this.event.claimBreakfastAmount = 0;
    this.event.claimDinnerAmount = 0;
    this.event.claimAccommodationAmount = 0;
    this.event.claimTollsAmount = 0;
    this.event.claimOtherAmount = 0;
    this.event.claimOtherDescription = "";
    this.event.expenseNotes = "";
  }

  // Customer charge = return distance for every training day, or one return
  // trip when accommodation is included.
  recalculateTransport(): void {
    if (!this.transportAutoReady) {
      return;
    }
    this.event.transportPrice = calculateCustomerTransportCharge(
      this.event.distance,
      this.durationDays,
      Boolean(this.event.accommodationIncluded),
      this.offSiteRates.customerTransportPerKm
    ).toFixed(2);
  }

  // Quote dropdown label — just the id, e.g. "#1234".
  quoteLabel(quote: any): string {
    if (!quote) {
      return "";
    }
    // A quote already booked for this item stays in the list, greyed out, so
    // the agent can see it exists rather than wonder where it went.
    const suffix = quote.alreadyBooked ? " — already booked" : "";
    return `#${quote.id} — ${quote.companyName}${suffix}`;
  }

  quoteDisabled(quote: any): boolean {
    return Boolean(quote && quote.alreadyBooked);
  }

  /**
   * The quote list only offers quotes that carry the exact item being booked,
   * and a course and an online course can share a name to the letter. Saying
   * so beats an empty dropdown with no explanation.
   */
  get noQuotesHint(): string {
    if (!this.event.eventableId) {
      return "Choose what is being booked first.";
    }
    const kind = (this.event.eventType?.name || "item").toLowerCase();
    return `No quote lists this ${kind}. Raise or amend a quote so it includes this exact ${kind} — a quote for the classroom course does not cover the online one, or the other way round.`;
  }

  async loadQuotes(search = ""): Promise<void> {
    const requestId = ++this.quoteRequestId;
    const bookingType = this.event.eventType?.name;
    const eventableId = this.event.eventableId;
    if (!this.event.eventType?.id || !this.event.eventableId) {
      this.quotes = [];
      this.quoteLoading = false;
      return;
    }

    this.quoteLoading = true;
    try {
      const filters: any = { limit: 1000 };
      // Courses, online courses and audits are all quoted the same way, so
      // every booking offers only the quotes that carry what it is booking —
      // this one's own quote included, which is why it is excluded by id.
      filters.bookingCourseId = this.event.eventableId;
      filters.bookingEventableType = this.event.eventType.eventableType;
      filters.bookingEventId = this.eventId;
      if (search) {
        filters.name = search;
      }
      const response: any = await Quotes.loadQuotesData(1, filters);
      if (
        requestId !== this.quoteRequestId ||
        bookingType !== this.event.eventType?.name ||
        eventableId !== this.event.eventableId
      )
        return;
      const selectedQuotes = this.quotes.filter((quote: any) =>
        (this.event.quoteIds || []).includes(quote.id)
      );
      const loadedQuotes = [...selectedQuotes, ...(response.data || [])];
      const missingIds = (this.event.quoteIds || []).filter(
        (id: number) => !loadedQuotes.some(quote => quote.id === id)
      );
      const missingQuotes = await Promise.all(
        missingIds.map((id: number) => Quotes.fetchQuoteById(id))
      );
      if (
        requestId !== this.quoteRequestId ||
        bookingType !== this.event.eventType?.name ||
        eventableId !== this.event.eventableId
      )
        return;
      this.quotes = [...loadedQuotes, ...missingQuotes].filter(
        (quote: any, index: number, values: any[]) =>
          values.findIndex(item => item.id === quote.id) === index
      );
    } catch (e) {
      if (requestId === this.quoteRequestId) await this.$error(e);
    } finally {
      if (requestId === this.quoteRequestId) this.quoteLoading = false;
    }
  }

  @Watch("quoteSearch")
  onQuoteSearch(value: string): void {
    if (this.quoteSearchTimer !== null) {
      window.clearTimeout(this.quoteSearchTimer);
    }
    this.quoteSearchTimer = window.setTimeout(() => {
      this.loadQuotes(value || "");
    }, 500);
  }

  onQuotesSelect(quoteIds: number[] = []): void {
    this.event.quoteIds = quoteIds || [];
    this.event.quoteId = this.event.quoteIds[0] || null;
    this.applySelectedQuotes();
  }

  applySelectedQuotes(): void {
    const selected: any[] = (this.event.quoteIds || []).map((id: number) =>
      this.quotes.find(quote => quote.id === id)
    );
    // Do not erase saved companies if a selected quote could not be loaded.
    if (selected.some(quote => !quote)) return;
    this.updCompanies(
      quoteBookingCompanies(
        selected,
        this.event.company || [],
        this.event.eventType.name,
        this.event.eventableId,
        this.coursesInfo?.price
      )
    );
  }

  get buttonsArray() {
    return [
      {
        text: "Back",
        icon: "mdi-chevron-left",
        color: "white",
        action: "back",
        disabled: false
      },
      {
        text: "Delete",
        icon: "mdi-delete",
        color: "gray",
        action: "delete",
        disabled: this.event.blockDeletion
      },
      {
        text: "Save",
        icon: "mdi-pencil-box-outline",
        color: "dark",
        action: "save",
        disabled: this.fieldsLocked
      }
    ];
  }
  get isOnlineCourse() {
    return this.event?.eventType?.name === "Online Course";
  }

  get isTransportDisabled(): boolean {
    return !this.addressRules;
  }
  get fieldsLocked(): boolean {
    return (
      !this.event.eventType?.id ||
      !this.event.eventableId ||
      !this.event.quoteIds?.length
    );
  }
  get availableCourses(): any[] {
    return this.courses || [];
  }
  get eventableItems(): any[] {
    if (this.event.eventType?.name === "Audit") {
      return this.audits || [];
    }
    if (this.event.eventType?.name === "Online Course") {
      return this.onlineCourses || [];
    }
    return this.availableCourses;
  }
  get eventableLabel(): string {
    if (this.event.eventType?.name === "Audit") {
      return "Select Audit";
    }
    if (this.event.eventType?.name === "Online Course") {
      return "Select Online Course";
    }
    return "Select Course";
  }
  /**
   * Where the booking can be delivered. An online course is only ever
   * online. A training course may be at our office, the customer's premise
   * or online. An audit is the customer's or ours and never online, so the
   * Online premise is withheld from it rather than merely discouraged.
   */
  get premiseOptions(): OfficeType[] {
    const eventTypeId = Number(this.event.eventType?.id);
    return (this.officeTypes || []).filter((officeType: OfficeType) => {
      const isOnline = Number(officeType.id) === OFFICE_TYPE_ONLINE;
      if (eventTypeId === EVENT_TYPE_ONLINE_COURSE) {
        return isOnline;
      }
      if (eventTypeId === EVENT_TYPE_AUDIT) {
        return !isOnline;
      }
      return true;
    });
  }

  get isCustomerPremise(): boolean {
    return Number(this.event.officeTypeId) === 2;
  }
  get eventId(): string {
    return this.$route.params.id;
  }
  get offlineTrainings() {
    return this.event?.offlineTrainings || [];
  }

  get addressRules(): string {
    const currentType = this.event?.officeTypeId || 0;
    const index = this.officeTypes.findIndex(
      (officeType: OfficeType) => officeType.id === currentType
    );
    if (index !== -1) {
      const selectedOfficeType: OfficeType = this.officeTypes[index];
      if (selectedOfficeType) {
        return selectedOfficeType.validateAddress ? "required" : "";
      }
    }
    return "";
  }
  get formattedEventStartDate() {
    return moment(this.eventStartDate, "YYYY-MM-DD").format("DD/MM/YYYY");
  }
  // get formattedEventEndDate() {
  //   return moment(this.event.eventFinishDate,"DD-MM-YYYY").format("DD/MM/YYYY");
  // }
  get checkUser() {
    const currentUser: any = this.getUser;
    const isManager =
      currentUser && currentUser.permissions
        ? Boolean(
            currentUser.permissions.find(
              (permission: string) => permission === "create_role"
            )
          )
        : false;
    return isManager;
  }
  get isOccupationalQualification() {
    return this.coursesInfo?.category?.id == this.OCCUPATIONAL_QUALIFICATION_ID;
  }

  @Watch("event.eventableId")
  async onSelectCourse(id: number, oldId: number) {
    if (this.loading) return;
    if (id) {
      if (oldId > 0 && Number(oldId) !== Number(id)) {
        this.event.instructorId = null;
        if (!this.loading && this.event?.eventType?.name === "Course") {
          this.event.quoteId = null;
          this.event.quoteIds = [];
          this.updCompanies([]);
          this.quotes = [];
        }
      }
      if (this.event?.eventType?.name === "Course") {
        await this.getCoursesList();
        await this.fetchCourseInfo(id);
      }

      if (this.event?.eventType?.name === "Audit") {
        await this.fetchAuditInfo(id);
        await this.getAuditsList().catch(e => {
          this.$error({ error: e });
        });
      }

      if (this.event?.eventType?.name === "Online Course") {
        await this.fetchOnlineCourseInfo(id);
        await this.getOnlineCoursesList().catch(e => {
          this.$error({ error: e });
        });
      }
      await this.loadQuotes();
      this.applySelectedQuotes();
    }
  }

  @Watch("coursesInfo")
  updCompaniesPrice() {
    if (this.coursesInfo && !this.loading) {
      if (this.event.eventType?.name === "Course") {
        this.applySelectedQuotes();
      }
    }
  }

  @Watch("event.company", { deep: true })
  editSumCard() {
    if (this.event.company?.length) {
      this.updPrice();
    }
    // const a = this.courseOverview;
    // if (a && a[3]?.content) {
    //   a[3].content = `${new Intl.NumberFormat("en", {
    //     style: "currency",
    //     currency: "ZAR",
    //     minimumFractionDigits: 2
    //   })
    //     .format(this.event.price)
    //     .substr(2)}`;
    // }
  }

  @Watch("selectedDelegates")
  updAssign() {
    this.updPrice();
    // const a = this.courseOverview;
    // if (a && a[3]?.content) {
    //   a[3].content = `${new Intl.NumberFormat("en", {
    //     style: "currency",
    //     currency: "ZAR",
    //     minimumFractionDigits: 2
    //   })
    //     .format(this.event.price)
    //     .substr(2)}`;
    // }
  }

  async getDelegateByEvent() {
    this.selectedDelegates = [];
    const delegates = await Delegates.delegatesForEvent(this.eventId);
    if (delegates.length) {
      delegates.forEach(
        (delegate: {
          id: number;
          name: string;
          surname: string;
          companyId: number;
        }) => {
          this.selectedDelegates.push({
            id: delegate.id,
            companyId: delegate.companyId
          });
        }
      );
    }
  }

  updPrice() {
    // let sum = 0;
    // this.selectedDelegates.forEach((d: any) => {
    //   this.event.company?.forEach((c: any) => {
    //     if (d.companyId === c.companyId) {
    //       sum += +c.price;
    //     }
    //   });
    // });
    // console.log("summ: ", sum / (this.selectedDelegates.length || 1));

    // this.event.price = sum / (this.selectedDelegates.length || 1);

    if (this.event.company) {
      const price = this.event.company.reduce((acc: number, company: any) => {
        return (acc += Number(company.price) || 0);
      }, 0);
      this.event.price = price.toFixed(2);
    }
  }

  formatDate(date: string | undefined): string {
    if (!date) return "";
    const [day, month, year] = date.split("-");
    return `${month}/${day}/${year}`;
  }

  async onGoBack() {
    await this.$router.push({ name: "CourseEvents" });
  }

  async onSave() {
    (this.$refs.form as Vue & {
      click: () => void;
    }).click();
  }

  onDurationSelect() {
    const localDurations: any = this.durations;
    this.courseOverview[1].content = localDurations.find(
      (duration: any) => duration.id === this.durationVal
    )?.name;
    // Days changed -> Transport may change when no accommodation.
    this.recalculateTransport();
  }

  async save() {
    try {
      this.updPrice();
      this.buttonsArray.forEach(btn => {
        btn.disabled = true;
      });
      this.event.transportPrice += "";
      const eventData = JSON.parse(JSON.stringify(this.event));

      eventData.eventStartDate = moment(
        this.eventStartDate,
        "YYYY-MM-DD"
      ).format("DD-MM-YYYY");
      eventData.eventableType = this.event.eventType?.eventableType;
      eventData.courseDurationId = this.courseDurationId;

      eventData.courseDurationId = this.durationVal.id || this.durationVal;
      delete eventData.duration;

      delete eventData.name;
      // eventData.courseDurationId = this.coursesInfo.durationDays || 1;
      // if (this.event.eventTypeId == 2) eventData.courseId = eventData.auditId;
      // if (!this.event.price) eventData.price = this.coursesInfo.price;

      await this.updEvent({
        eventData,
        selectedDelegates: this.selectedDelegates
      });
      await this.$success("<strong>Success!</strong> Event has been updated!");

      if (this.$route.name === "EditEvent") {
        await this.$router.push({ name: "CourseEvents" });
      }
    } catch (e) {
      (this.$refs.eventForm as Vue & {
        setErrors: (errors: any) => void;
      }).setErrors(e);
      await this.$error(e);
    } finally {
      this.buttonsArray.forEach(btn => {
        btn.disabled = false;
      });
    }
  }

  getCompaniesGoal(data: any) {
    if (data) {
      this.event.company = data;
    }
  }

  async onDelete() {
    this.deleteDialogOpen = true;
  }

  async onDeleteConfirmed(payload: {
    reason: string;
    instructorArrived: boolean | null;
  }) {
    this.deleting = true;
    try {
      await this.removeEvent({ eventId: this.eventId, ...payload });
      this.deleteDialogOpen = false;
      await this.$success("Event has been deleted!");
      await this.onGoBack();
    } catch (e) {
      await this.$error(e);
    } finally {
      this.deleting = false;
    }
  }

  async handleCourseChange() {
    const eventData: any = this.event;
    if (this.event?.eventType?.name === "Course") {
      this.getCoursesList();
      await this.fetchCourseInfo(eventData.course.id.toString());
      this.selectedCourseCatId = this.coursesInfo.category.id;
    }
    if (this.event?.eventType?.name === "Audit") {
      this.getAuditsList();
      await this.fetchAuditInfo(eventData.audit.id.toString());
    }
    if (this.event?.eventType?.name === "Online Course") {
      this.getOnlineCoursesList();
      await this.fetchOnlineCourseInfo(eventData.onlineCourse.id.toString());
    }
  }

  async fetchAuditInfo(id: number) {
    try {
      await this.getInstructorslistByAuditID({ id, active: 1 });
      await this.getAuditInfo(id.toString());
      this.courseDurationId = this.coursesInfo.duration;
    } catch (e) {
      await this.$error([e]);
    }
  }

  async fetchCourseInfo(id: number) {
    try {
      await this.getInstructorslistByCourseID({ id, active: 1 });
      await this.getCoursesInfo(id.toString());
      const course = this.coursesInfo;
      this.selectedCourseCatId = course.category.id;

      const durationName =
        this.coursesInfo.category.id === 3 ? "" : course.duration;
      const price = `${new Intl.NumberFormat("en", {
        style: "currency",
        currency: "ZAR",
        minimumFractionDigits: 2
      }).format(course.price)}`;

      this.courseOverview = [
        { title: "Name", content: course?.name },
        {
          title: "Duration",
          content: durationName?.name
        },
        { title: "Certificate", content: course.courseType },
        {
          title: "Catalogue Cost Per Day",
          content: price
        },
        {
          title: course.dol ? "DOL #" : "",
          content: course.dol ? course.dol : ""
        }
      ];
      // if (this.coursesInfo.category.id === 3) {
      this.durationVal = this.event.duration;
      this.onDurationSelect();
      // }
    } catch (e) {
      await this.$error(e);
    }
  }

  async fetchOnlineCourseInfo(id: number) {
    try {
      await this.getInstructorslistByOnlineCourseID({ id, active: 1 });
      await this.getOnlineCoursesInfo(id.toString());

      const course = this.coursesInfo;
      this.courseDurationId = course.duration.id;
      const durationName =
        this.coursesInfo.category.id === 3 ? "" : course.duration;
      this.courseOverview = [
        { title: "Name", content: course?.name },
        {
          title: "Duration",
          content: durationName?.name
        },
        { title: "Certificate", content: course?.type?.name },
        {
          title: "Cost Per Day",
          content: `${new Intl.NumberFormat("en", {
            style: "currency",
            currency: "ZAR",
            minimumFractionDigits: 2
          })
            .format(this.coursesInfo.price || 0)
            .substr(2)}`
        },
        {
          title: course.dol ? "DOL #" : "",
          content: course.dol ? course.dol : ""
        }
      ];
      if (this.coursesInfo.category.id === 3) {
        this.durationVal = course.duration;
      }
    } catch (e) {
      await this.$error(e);
    }
  }

  removeAssign(company: number) {
    const newDelegates: { id: number; companyId: number }[] = [];
    this.selectedDelegates.forEach(
      (d: { id: number; companyId: number }, index: number) => {
        if (d.companyId !== company) {
          this.selectedDelegates.splice(index, 1);
          newDelegates.push(d);
        }
      }
    );
    this.selectedDelegates = newDelegates;
  }

  get canEdit() {
    const currentUser: any = this.getUser;
    const isPermitted = Boolean(
      currentUser.permissions.find(
        (permission: string) => permission === "edit_event"
      )
    );

    return isPermitted;
  }
}
